// src/contexts/CartContext.jsx
import { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

// Helper function to detect language based on domain
const detectLanguage = () => {
  // Check if we're in a browser environment
  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname;
    // If the domain ends with .com, use English
    if (hostname.endsWith('.com')) {
      return 'en';
    }
  }
  
  // Default to Czech or use stored preference
  return localStorage.getItem('userLanguage') || 'cs';
};

export const CartProvider = ({ children }) => {
  // Initialize cart from localStorage immediately to prevent flickering empty cart
  const initialCart = (() => {
    try {
      const savedCart = localStorage.getItem('cart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error('Error loading initial cart from localStorage:', error);
      return [];
    }
  })();

  const [cart, setCart] = useState(initialCart);
  const [cartNotification, setCartNotification] = useState({
    isVisible: false,
    productName: null
  });

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      // Use a more descriptive key to avoid conflicts with other applications
      localStorage.setItem('norisknodrift_cart', JSON.stringify(cart));
      console.log('Cart saved to localStorage:', cart);
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [cart]);

  // Debug: Log when the component mounts to verify initialization
  useEffect(() => {
    console.log('CartContext initialized with cart:', cart);
  }, []);

  const addToCart = (product) => {
    console.log('Adding to cart:', product);
    
    setCart((prevCart) => {
      const existingProduct = prevCart.find((item) => item.id === product.id);
      const newCart = existingProduct
        ? prevCart.map((item) =>
            item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
          )
        : [...prevCart, { ...product, quantity: 1 }];
        
      // Additional safety measure: immediately save to localStorage
      try {
        localStorage.setItem('norisknodrift_cart', JSON.stringify(newCart));
      } catch (error) {
        console.error('Error directly saving cart to localStorage:', error);
      }
      
      return newCart;
    });

    // Get language based on domain first, then fallback to stored preference
    const language = detectLanguage();
    
    // Get product name based on language
    let productName = product.name;
    
    if (product.translations && product.translations[language]) {
      productName = product.translations[language].name;
    } else if (product.translations && product.translations.en) {
      productName = product.translations.en.name;
    }
    
    // Show notification
    setCartNotification({
      isVisible: true,
      productName
    });
  };

  const removeFromCart = (productId) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === productId ? { ...item, quantity: Math.max(1, quantity) } : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    // Also clear from localStorage
    localStorage.removeItem('norisknodrift_cart');
  };

  const closeNotification = () => {
    setCartNotification({
      isVisible: false,
      productName: null
    });
  };

  // Calculate total items in cart (for display in navbar)
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider 
      value={{ 
        cart, 
        addToCart, 
        removeFromCart, 
        updateQuantity, 
        clearCart, 
        cartNotification, 
        closeNotification,
        totalItems
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);